export interface PublicEdgeBaseUrlInput {
  configuredBaseUrl?: string | null;
  runtimeHostname?: string | null;
}

const PUBLIC_EDGE_BY_ENV = {
  dev: 'https://dev-api.segmently.ai',
  stage: 'https://stage-api.segmently.ai',
  prod: 'https://api.segmently.ai',
} as const;

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

export const normalizeUrlBase = (value: string): string => value.replace(/\/+$/, '');

export const isLocalhostUrl = (value?: string | null): boolean => {
  if (!value) return false;

  try {
    const url = new URL(value);
    return LOCAL_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
};

export const inferPublicEdgeBaseUrlFromHostname = (hostname?: string | null): string => {
  const normalizedHostname = (hostname || '').toLowerCase();

  if (
    normalizedHostname.includes('devsegmentlyai') ||
    normalizedHostname.includes('dev-api')
  ) {
    return PUBLIC_EDGE_BY_ENV.dev;
  }

  if (
    normalizedHostname.includes('stagesegmentlyai') ||
    normalizedHostname.includes('stage-api')
  ) {
    return PUBLIC_EDGE_BY_ENV.stage;
  }

  if (
    normalizedHostname.includes('app.segmently.ai') ||
    normalizedHostname === 'segmently.ai' ||
    normalizedHostname.endsWith('.segmently.ai') ||
    normalizedHostname.includes('cohortkeyai') ||
    normalizedHostname === 'api.segmently.ai'
  ) {
    return PUBLIC_EDGE_BY_ENV.prod;
  }

  return PUBLIC_EDGE_BY_ENV.dev;
};

/**
 * Resolve the public Edge Gateway URL used for Cloud URL and SDK-config links.
 *
 * Local preview links use a separate helper (`getLocalDevFunnelUrl`). If a
 * local dev env accidentally leaves VITE_EDGE_GATEWAY_URL on localhost, this
 * resolver falls back to the public environment URL so Cloud URL surfaces never
 * become same-machine links.
 */
export const resolvePublicEdgeGatewayBaseUrl = ({
  configuredBaseUrl,
  runtimeHostname,
}: PublicEdgeBaseUrlInput): string => {
  if (configuredBaseUrl && !isLocalhostUrl(configuredBaseUrl)) {
    return normalizeUrlBase(configuredBaseUrl);
  }

  return inferPublicEdgeBaseUrlFromHostname(runtimeHostname);
};
